import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import AuthRoutes from "./config/routes/AuthRoutes";
import "./App.css";
import Toast from "./components/Toast";
import FullPageLoader from "./components/FullPageLoader";

import { onMessage } from "firebase/messaging";
import {
  checkMessagingSupport,
  generateFCMToken,
  getMessagingInstance,
} from "./config/firebase/firebase";

import {
  setAppLoading,
  setInfo,
  incrementUnreadNotificationsCount,
  setMessage,
  setError,
  setIsPushNotificationSupported,
} from "./store/common/generalSlice";
import { setSelectedBookingListStatus } from "./store/common/bookingSlice";
import { getSocketInstance } from "./config/hooks/utility/socket";
import {
  generateFallbackDeviceToken,
  getFcmToken,
  setChannelUnreadMsgsCount,
} from "./config/utils";
import { getMyChatChannels } from "./store/common/chatSlice/api";
import { setSelectedChannelId } from "./store/common/chatSlice";
import { IoIosClose } from "react-icons/io";
import NotificationBanner from "./components/NotificationBanner";

const App = ({ swRegistration }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { loading, isPushNotificationSupported } = useSelector(
    (state) => state.general,
  );
  const selectedBookingListStatus = useSelector(
    (state) => state.booking.selectedBookingListStatus,
  );
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const selectedChannelId = useSelector(
    (state) => state.chats.selectedChannelId,
  );

  const [notifications, setNotifications] = useState(
    typeof Notification !== "undefined"
      ? Notification?.permission
      : "unsupported",
  );
  const [
    showPushNotificationSupportAlert,
    setShowPushNotificationSupportAlert,
  ] = useState(false);
  const [notificationSetupComplete, setNotificationSetupComplete] =
    useState(false);
  const [tmp, setTmp] = useState([]);

  const handleEnableNotifications = async () => {
    try {
      // First check if FCM is supported in this browser
      const fcmSupported = await checkMessagingSupport();

      if (fcmSupported) {
        const success = await generateFCMToken(swRegistration);
        if (success) {
          const permission = Notification?.permission;
          setNotifications(permission);
          dispatch(setMessage("Notifications enabled!"));
        } else {
          dispatch(
            setMessage(
              "Failed to enable notifications. Using fallback method.",
            ),
          );
          const fallbackToken = generateFallbackDeviceToken();
          if (fallbackToken) {
            localStorage.setItem("fcmToken", fallbackToken);
            dispatch(setMessage("Alternative notification system activated."));
          }
        }
      } else {
        // If FCM isn't supported, use fallback token
        const fallbackToken = generateFallbackDeviceToken();
        if (fallbackToken) {
          localStorage.setItem("fcmToken", fallbackToken);
          dispatch(
            setMessage("Basic notification system activated for this browser."),
          );
        }
      }
    } catch (error) {
      console.error("Error enabling notifications:", error);
      dispatch(
        setError("Could not enable notifications. Please try again later."),
      );
    }
  };

  const closeBrowserSupportMessage = () => {
    setShowPushNotificationSupportAlert(false);
    localStorage.setItem("hideNotificationSupportAlert", "true");
  };

  const getFCMTokenMethod = async () => {
    const hasFCMToken = await getFcmToken();
    return hasFCMToken;
  };

  // Check push notification support on component mount
  useEffect(() => {
    const checkNotificationSupport = async () => {
      const supported = await checkMessagingSupport();
      dispatch(setIsPushNotificationSupported(supported));
      return supported;
    };

    checkNotificationSupport();
  }, [dispatch]);

  // FCM Foreground Notifications - Only set up if messaging is supported
  useEffect(() => {
    const setupForegroundNotifications = async () => {
      const messaging = getMessagingInstance();

      if (!messaging) {
        // No need to show error - we'll use fallback methods
        return null;
      }

      try {
        return onMessage(messaging, (payload) => {
          console.log("from app.jsx", payload);
          const notificationData = payload?.data?.data;

          if (notificationData?.includes("channelId")) {
            if (!location?.pathname.includes("chat")) {
              dispatch(setInfo("You have received a new message"));
            }
          } else if (payload?.notification) {
            const parsedData = JSON.parse(notificationData || "{}");
            if (parsedData.notificationId) {
              dispatch(incrementUnreadNotificationsCount(parsedData));
            }
            dispatch(setInfo(payload.notification.body));
          }
        });
      } catch (error) {
        console.log("Error setting up foreground notifications:", error);
        return null;
      }
    };

    const unsubscribePromise = setupForegroundNotifications();

    return () => {
      unsubscribePromise.then((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  // Push notification browser permission
  useEffect(() => {
    if (typeof Notification !== "undefined") {
      const permission = Notification?.permission;
      setNotifications(permission);
    } else {
      setNotifications("unsupported");
    }
  }, [dispatch]);

  // App loading indicator cleanup
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => dispatch(setAppLoading(false)), 2000);
      return () => clearTimeout(timeout);
    }
  }, [loading, dispatch]);

  // Booking and chat cleanup on route change
  useEffect(() => {
    if (isLoggedIn && location?.pathname) {
      if (
        selectedBookingListStatus &&
        !/booking\/my-bookings/.test(location.pathname)
      ) {
        dispatch(setSelectedBookingListStatus(null));
      }

      if (!location.pathname.includes("chats")) {
        dispatch(setSelectedChannelId(-1));
      }
    }
  }, [location.pathname, isLoggedIn, selectedBookingListStatus, dispatch]);

  // Fetch channels silently on login so unread counts & side-menu dot are set on any page
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getMyChatChannels(false));
    }
  }, [isLoggedIn]);

  // Socket init + unread message tracking
  useEffect(() => {
    if (!isLoggedIn) return;

    getSocketInstance();

    const handleUnreadMessages = (event) => {
      const data = event.detail;
      if (data?.channelId && selectedChannelId !== data.channelId) {
        setChannelUnreadMsgsCount(data);
        // Show in-app toast notification when user is not in the chats page
        if (!location?.pathname?.includes("chats")) {
          dispatch(setInfo("You have received a new message"));
        }
      }
    };

    window.addEventListener("socket:message:receive", handleUnreadMessages);

    return () => {
      window.removeEventListener(
        "socket:message:receive",
        handleUnreadMessages,
      );
    };
  }, [isLoggedIn, selectedChannelId]);

  // Enhanced notification setup
  useEffect(() => {
    // Prevent duplicate setup
    if (notificationSetupComplete) return;

    // Check if the alert should be shown based on user preference and login page
    const shouldHideAlert =
      localStorage.getItem("hideNotificationSupportAlert") === "true";
    setShowPushNotificationSupportAlert(
      !isPushNotificationSupported && !shouldHideAlert,
    );

    const setupNotifications = async () => {
      try {
        // Check if we already have a token
        const existingToken = await getFCMTokenMethod();

        // If we have permission but no token, try to generate one
        if (
          (notifications === "granted" || notifications === "unsupported") &&
          !existingToken
        ) {
          const fcmSupported = await checkMessagingSupport();
          if (fcmSupported) {
            // Firebase FCM Token generation
            await generateFCMToken(swRegistration);
          } else {
            // Fallback device token
            const fallbackDeviceToken = generateFallbackDeviceToken();
            if (fallbackDeviceToken) {
              localStorage.setItem("fcmToken", fallbackDeviceToken);
            } else {
              // Only show error if user has explicitly granted permission
              dispatch(
                setError(
                  "Unable to generate device token. Some features may be limited.",
                ),
              );
            }
          }
        }

        setNotificationSetupComplete(true);
      } catch (error) {
        console.log("Error in notification setup:", error);
      }
    };

    setupNotifications();
  }, [
    notifications,
    swRegistration,
    isLoggedIn,
    isPushNotificationSupported,
    notificationSetupComplete,
    dispatch,
  ]);

  console.log("Latest update 26/11/2025 By Burhan");

  // Determine whether to show the browser support alert
  const shouldShowBrowserAlert =
    showPushNotificationSupportAlert &&
    (location?.pathname === "/login" || location?.pathname === "/signup");

  return (
    <div>
      {loading && <FullPageLoader />}
      <Toast />

      {/* Notification Permission Banner */}
      {notifications === "default" && (
        <NotificationBanner
          message="Get notified about new messages and updates"
          actionText="Enable"
          onAction={handleEnableNotifications}
        />
      )}

      {/* Permission Denied Banner */}
      {notifications === "denied" && (
        <NotificationBanner
          message="Notifications are blocked. Please enable them from your browser settings."
          type="warning"
        />
      )}

      {/* Browser Support Alert - Only on login/signup pages */}
      {shouldShowBrowserAlert && (
        <div className="flex px-4 py-2 justify-between items-center bg-amber-50 border-b border-amber-200">
          <p className="text-amber-800">
            This browser has limited support for notifications. Some features
            may not work as expected.
          </p>
          <IoIosClose
            size={24}
            onClick={closeBrowserSupportMessage}
            className="cursor-pointer text-amber-800"
          />
        </div>
      )}
      <AuthRoutes />
    </div>
  );
};

export default App;
